/**
 * useContactListFilterOptions.ts
 *
 * Thin router that delegates to the same shared hooks each list page uses,
 * guaranteeing filter options are always identical across the application.
 */
import type { FilterOption } from '@/types/filters.types';
import { usePlanFilterOptions } from '@/hooks/filters/usePlanFilterOptions';
import { usePatientFilterOptions } from '@/hooks/filters/usePatientFilterOptions';
import { useAppointmentFilterOptions } from '@/hooks/filters/useAppointmentFilterOptions';
import { useMembershipFilterOptions } from '@/hooks/filters/useMembershipFilterOptions';
import { usePackageFilterOptions } from '@/hooks/filters/usePackageFilterOptions';
import { usePaymentFilterOptions } from '@/hooks/filters/usePaymentFilterOptions';
import { useIntegrationOrderFilterOptions } from '@/hooks/filters/useIntegrationOrderFilterOptions';
import { AXIOM_TILE_STATUSES, RMM_TILE_STATUSES } from '@/pages/marketplace/integrationOrdersShared';

const EMPTY: Record<string, FilterOption[]> = {};
const MARKETPLACE_TILE_STATUSES = [...AXIOM_TILE_STATUSES, ...RMM_TILE_STATUSES];

export function useContactListFilterOptions(sourceType: string): Record<string, FilterOption[]> {
  // Always fetch all options upfront so they're ready immediately when the
  // user switches data source — avoids React Query stale-cache issues with
  // enabled toggling from false → true.
  const planOpts = usePlanFilterOptions();
  const patientOpts = usePatientFilterOptions();
  const appointmentOpts = useAppointmentFilterOptions();
  const membershipOpts = useMembershipFilterOptions();
  const packageOpts = usePackageFilterOptions();
  const paymentOpts = usePaymentFilterOptions();
  // Only when needed: most lists never use this source.
  // The pages' tile statuses too, so a list saved from a tile shows its Status ticked.
  const marketplaceOpts = useIntegrationOrderFilterOptions({
    enabled: sourceType === 'integration_order',
    ensureStatuses: MARKETPLACE_TILE_STATUSES,
  });

  switch (sourceType) {
    case 'plan':
    case 'inquiry':
      return planOpts;
    case 'patient':
    case 'app_users':
      return patientOpts;
    case 'appointment':
      return appointmentOpts;
    case 'membership':
    case 'member':
      return membershipOpts;
    case 'package':
      return packageOpts;
    case 'payment':
      return paymentOpts;
    case 'integration_order':
      return marketplaceOpts;
    default:
      return EMPTY;
  }
}
